import { memo, useEffect, useRef } from "react"
import { useMedia } from "react-use"

import { cn } from "@productlane/lib/src/cn"
import { Empty, Loader } from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"

import type { ThreadContextState } from "../../threads/thread-context"
import EmptyInbox from "@/assets/empty-states/inbox/empty-inbox.png"
import { MetaInfo } from "@/components/meta-info"
import {
	PANEL_SPECS,
	panelWidthStyle,
} from "@/components/panel-resize/panel-resize"
import { PanelResizeHandle } from "@/components/panel-resize/panel-resize-handle"
import { focusModeWidth } from "@/constants"
import { usePanelWidthState } from "@/lib/states/panel-width-state"
import { useHoldThread } from "@/lib/use-thread-lock"
import { useZero } from "@/lib/zero"
import { useMarkThreadNotificationsRead } from "@/screens/app/notifications/use-mark-thread-notifications-read"
import { CenterContent } from "../../threads/components/center-content/center-content"
import { getThreadDocumentTitle } from "../../threads/components/center-content/thread-header-name"
import { PersistentAiChatOverlay } from "../../threads/components/right-bar/ai-chat/persistent-ai-chat-overlay"
import { RightBar } from "../../threads/components/right-bar/right-bar"
import { useInitThreadForId } from "../../threads/hooks/useInitThread"
import { ThreadContextProvider } from "../../threads/thread-context"

interface ThreadDetailPanelProps {
	threadId: string
}

// The inbox list re-renders on every scroll event and Zero update. Memo keeps
// the open conversation out of those renders.
export const ThreadDetailPanel = memo(function ThreadDetailPanel({
	threadId,
}: ThreadDetailPanelProps) {
	const zero = useZero()
	const isLarge = useMedia(focusModeWidth)
	const prevThreadIdRef = useRef<string | null>(null)
	const prevInboundAtRef = useRef<number | null>(null)
	const getThreadState = useInitThreadForId as (
		id: string,
	) => ThreadContextState
	const threadState = getThreadState(threadId)

	const currentThreadId = threadState.thread?.id
	const currentThreadIsRead = threadState.thread?.isRead
	const lastInboundMessageAt = threadState.thread?.lastInboundMessageAt

	useHoldThread(currentThreadId)
	useMarkThreadNotificationsRead(threadId)

	// Mark read when opening an unread thread, and again when a new inbound
	// message lands while the thread is open. Gating on inbound arrival (not
	// isRead alone) keeps a manual mark-as-unread from bouncing back to read.
	useEffect(() => {
		const isNewThread = prevThreadIdRef.current !== currentThreadId
		const inboundAt = lastInboundMessageAt ?? null
		const hasNewInbound =
			!isNewThread &&
			inboundAt !== null &&
			prevInboundAtRef.current !== null &&
			inboundAt !== prevInboundAtRef.current

		if (
			currentThreadId &&
			!currentThreadIsRead &&
			(isNewThread || hasNewInbound)
		) {
			zero.mutate(
				mutators.threads.update({
					id: currentThreadId,
					isRead: true,
				}),
			)
		}
		prevThreadIdRef.current = currentThreadId ?? null
		prevInboundAtRef.current = inboundAt
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentThreadId, currentThreadIsRead, lastInboundMessageAt, zero.mutate])

	const detailsWidth = usePanelWidthState((s) => s.widths["thread-details"])
	const detailRootRef = useRef<HTMLDivElement>(null)
	const detailGridRef = useRef<HTMLDivElement>(null)

	if (!threadState.thread) {
		const isComplete = threadState.queryResult.type === "complete"

		if (isComplete) {
			return (
				<div className="relative flex h-full w-full">
					<Empty
						image={
							<img
								src={EmptyInbox}
								className="h-auto w-[4.05rem] select-none object-contain"
								alt="Inbox"
							/>
						}
						title="Thread not found"
						text="We couldn't find the thread you're looking for."
						className="flex w-full justify-center"
					/>
				</div>
			)
		}

		return (
			<div className="flex flex-1 items-center justify-center">
				<Loader size={12} />
			</div>
		)
	}

	return (
		<>
			<MetaInfo title={getThreadDocumentTitle(threadState.thread)} />
			<ThreadContextProvider value={threadState}>
				<div
					ref={detailRootRef}
					style={
						isLarge
							? panelWidthStyle(PANEL_SPECS["thread-details"], detailsWidth)
							: undefined
					}
					className="relative flex flex-1 overflow-hidden [--thread-details-width:20rem] min-[1440px]:[--thread-details-width:22rem]"
				>
					<div
						ref={detailGridRef}
						className={cn(
							"flex-1 overflow-hidden",
							isLarge
								? "grid grid-cols-[minmax(0,1fr)_var(--thread-details-width,22rem)]"
								: "flex",
						)}
					>
						<CenterContent embedded />
						{!isLarge && <RightBar asDialog />}
						{isLarge && <RightBar />}
					</div>
					{isLarge && <PersistentAiChatOverlay />}
					{isLarge && (
						<PanelResizeHandle
							panel="thread-details"
							label="Resize thread details"
							panelSide="right"
							getPanel={() => detailsIn(detailGridRef.current)}
							getVarTarget={() => detailRootRef.current}
							getAbsorber={() => conversationIn(detailGridRef.current)}
							style={{
								right: "calc(var(--thread-details-width, 22rem) - 8px)",
							}}
						/>
					)}
				</div>
			</ThreadContextProvider>
		</>
	)
})
export const conversationIn = (root: HTMLElement | null) =>
	root?.querySelector<HTMLElement>("[data-thread-conversation]") ?? null

const detailsIn = (root: HTMLElement | null) =>
	root?.querySelector<HTMLElement>("[data-thread-details]") ?? null
